import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";
import { cx } from "../internal/cx.js";

export type TabsVariant = "underline" | "pill" | "segmented";
export type TabsSize = "sm" | "md" | "lg";

interface TabsContextValue {
  variant: TabsVariant;
  size: TabsSize;
}

const TabsContext = React.createContext<TabsContextValue>({
  variant: "pill",
  size: "md",
});

export interface TabsProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {
  variant?: TabsVariant;
  size?: TabsSize;
}

export const Tabs = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Root>, TabsProps>(
  function Tabs({ className, size = "md", variant = "pill", ...props }, ref) {
    const value = React.useMemo(() => ({ variant, size }), [variant, size]);
    return (
      <TabsContext.Provider value={value}>
        <TabsPrimitive.Root ref={ref} className={cx("cw-v2-tabs", className)} {...props} />
      </TabsContext.Provider>
    );
  },
);
Tabs.displayName = "Tabs";

export const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(function TabsList({ className, ...props }, ref) {
  const { variant, size } = React.useContext(TabsContext);
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cx(
        "cw-v2-tabs__list",
        `cw-v2-tabs__list--${variant}`,
        `cw-v2-tabs__list--${size}`,
        className,
      )}
      {...props}
    />
  );
});
TabsList.displayName = "TabsList";

export const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(function TabsTrigger({ className, ...props }, ref) {
  const { variant, size } = React.useContext(TabsContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cx(
        "cw-v2-tabs__trigger",
        `cw-v2-tabs__trigger--${variant}`,
        `cw-v2-tabs__trigger--${size}`,
        className,
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = "TabsTrigger";

export const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(function TabsContent({ className, ...props }, ref) {
  return (
    <TabsPrimitive.Content ref={ref} className={cx("cw-v2-tabs__content", className)} {...props} />
  );
});
TabsContent.displayName = "TabsContent";
